// Shared styling for everything that opens over the page — the card overflow
// ("⋯") menus, select popovers, suggestion lists — so every one matches the
// dialog family: tight corners, hairline border, Roboto, subtle hover.
import { Menu, MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { styled } from '@mui/system';
import {
  BORDER, HOVER_BG, MUTED, SANS_STACK, TEXT_DARK,
} from '../tokens';

// The surface every popover in the app sits on — context menu, select popover,
// suggestion list.
const POPOVER_PAPER = {
  borderRadius: 4,
  border: `1px solid ${BORDER}`,
  boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)',
  marginTop: '4px',
};

// A row in one of those popovers.
const POPOVER_ROW = {
  fontFamily: SANS_STACK,
  fontSize: 15,
  color: TEXT_DARK,
  paddingTop: '7px',
  paddingBottom: '7px',
};

export const AppMenu = styled(Menu)({
  '& .MuiPaper-root': {
    ...POPOVER_PAPER,
    fontFamily: SANS_STACK,
    minWidth: 160,
  },
  '& .MuiList-root': {
    paddingTop: 4,
    paddingBottom: 4,
  },
});

export const AppMenuItem = styled(MenuItem)({
  ...POPOVER_ROW,
  '&:hover': { backgroundColor: HOVER_BG },
});

// For MUI Select popovers (which render their own Menu, not <AppMenu>): pass as
// `MenuProps` so the dropdown paper + items match the card menus, with a coral
// tint on the selected row instead of MUI's default blue highlight.
export const selectMenuProps = {
  elevation: 0,
  // A function so the selected-row tint can be derived from the theme's coral;
  // MUI resolves an sx callback against the active theme.
  sx: (theme) => ({
    '& .MuiPaper-root': POPOVER_PAPER,
    '& .MuiMenuItem-root': {
      ...POPOVER_ROW,
      '&:hover': { backgroundColor: HOVER_BG },
      '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
      '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
    },
  }),
};

/**
 * For an `Autocomplete`'s suggestion popup: pass as `slotProps`. Its rows are
 * the picker's own `<li>`s, not `MenuItem`s.
 */
export const autocompletePopupSlotProps = {
  paper: {
    elevation: 0,
    sx: (theme) => ({
      ...POPOVER_PAPER,
      '& .MuiAutocomplete-listbox': { padding: '4px 0' },
      '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
        ...POPOVER_ROW,
        paddingLeft: '12px',
        paddingRight: '12px',
        '&:hover': { backgroundColor: HOVER_BG },
        // The picker moves this class with the pointer as well as the arrow
        // keys, so it covers both ways of landing on a row.
        '&.Mui-focused': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
      },
      '& .MuiAutocomplete-noOptions': {
        ...POPOVER_ROW,
        color: MUTED,
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    }),
  },
};
